import React, { useState } from 'react';
import type { PhotoAnnotation } from '../types/photo.types';

interface AnnotatedPhotoViewProps {
  imageUrl: string;
  annotation?: PhotoAnnotation;
  className?: string;
  compact?: boolean;
  /** object-fit mode for the image */
  objectFit?: 'cover' | 'contain';
  /** Externally controlled selection — when provided, takes precedence over internal state. */
  activePinId?: string | null;
  onPinClick?: (pinId: string | null) => void;
}

export const AnnotatedPhotoView: React.FC<AnnotatedPhotoViewProps> = ({
  imageUrl,
  annotation,
  className = '',
  compact = false,
  objectFit = 'cover',
  activePinId: externalActivePinId,
  onPinClick,
}) => {
  const [internalActivePinId, setInternalActivePinId] = useState<string | null>(null);
  const isControlled = externalActivePinId !== undefined;
  const activePinId = isControlled ? externalActivePinId ?? null : internalActivePinId;
  const setActivePin = (next: string | null) => {
    if (!isControlled) setInternalActivePinId(next);
    onPinClick?.(next);
  };

  if (!annotation || (annotation.drawings.length === 0 && annotation.pins.length === 0)) {
    return <img src={imageUrl} alt="Submission" className={className} style={{ width: '100%', height: '100%', objectFit }} />;
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }} className={className}>
      <img
        src={imageUrl}
        alt="Submission"
        style={{ width: '100%', height: '100%', objectFit }}
        onClick={() => setActivePin(null)}
      />

      {/* Drawing overlay */}
      <svg
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      >
        {annotation.drawings.map(p => (
          <polyline
            key={p.id}
            points={p.points.map(pt => `${pt.x},${pt.y}`).join(' ')}
            stroke={p.color}
            strokeWidth={p.strokeWidth / 1000}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
      </svg>

      {/* Pin markers — full mode: tappable with label popover */}
      {!compact && annotation.pins.map(pin => (
        <React.Fragment key={pin.id}>
          <div
            className="annotated-pin"
            onClick={(e) => { e.stopPropagation(); setActivePin(activePinId === pin.id ? null : pin.id); }}
            style={{
              position: 'absolute',
              left: `${pin.x * 100}%`,
              top: `${pin.y * 100}%`,
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: activePinId === pin.id ? '#1A1814' : '#D4715A',
              color: 'white',
              fontSize: 11,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: 'translate(-50%, -50%)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              cursor: 'pointer',
              zIndex: activePinId === pin.id ? 10 : 5,
              transition: 'background 0.15s, transform 0.15s',
            }}
          >
            {pin.index}
            {pin.audioUrl && (
              <span className="annotated-pin-mic-badge" aria-label="Voice note">
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3}>
                  <rect x="9" y="2" width="6" height="12" rx="3" />
                  <path d="M5 10a7 7 0 0 0 14 0" />
                </svg>
              </span>
            )}
          </div>
          {/* Label popover */}
          {activePinId === pin.id && pin.label && (
            <div
              onClick={e => e.stopPropagation()}
              style={{
                position: 'absolute',
                left: `${Math.min(Math.max(pin.x * 100, 15), 85)}%`,
                top: `${pin.y * 100 + 4}%`,
                transform: 'translateX(-50%)',
                background: 'white',
                borderRadius: 10,
                padding: '8px 12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                zIndex: 20,
                maxWidth: 200,
                fontSize: 13,
                fontWeight: 500,
                color: '#1A1814',
                lineHeight: 1.4,
              }}
            >
              <span style={{ fontSize: 10, fontWeight: 700, color: '#D4715A', marginRight: 6 }}>{pin.index}</span>
              {pin.label}
            </div>
          )}
        </React.Fragment>
      ))}

      {/* Pin count badge (compact mode — for thumbnails) */}
      {compact && annotation.pins.length > 0 && (
        <div style={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          background: '#D4715A',
          color: 'white',
          fontSize: 9,
          fontWeight: 700,
          borderRadius: 8,
          padding: '2px 6px',
        }}>
          {annotation.pins.length} pin{annotation.pins.length > 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};
